import { useHistory, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useMutation } from '@apollo/react-hooks';
import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';
import {
  currentLocationPathNameSelector,
  previousLocationPathNameSelector
} from 'store/router/selectors';
import { PreRegistrationFacilitySelector } from 'store/preRegistration/selectors';
import { RootState } from 'store/types';
import { Alert } from 'components/Alert';
import React, { ComponentType } from 'react';
import { connect, useDispatch } from 'react-redux';
import { FontSize } from 'modules/styles/variables';
import { MuiBox } from 'theme/material-ui';
import { PRE_REG_CHECK_IN } from 'services/orchestration/mutations';
import { PRE_REGISTRATION_JWT_TOKEN } from 'store/preRegistration/actions';
import { HeadingTypography } from '../styled';
import { CheckListItemPageProps } from '../Insurance/types';
import { PreRegHeader } from '../common';
import { AuthLiteLogin } from 'components/AuthLite';
import { CheckInForm } from 'components/AuthLite/types';

const ErrorInUUID = () => {
  return (
    <MuiBox flexDirection="column" alignContent="center" p={1}>
      <HeadingTypography fontSize={FontSize.large} style={{ marginLeft: 0 }}>
        Something seems to have gone wrong. Please tap the link in the Pre-registration text you
        received to get to the Pre-registration Sign-in page.
      </HeadingTypography>
    </MuiBox>
  );
};

export function CheckIn({ currentUrl, referringUrl, facilityLocation }: CheckListItemPageProps) {
  const { eventId } = useParams<{ eventId: string }>();
  const dispatch = useDispatch();
  const [attempts, setAttempts] = React.useState(0);

  // Check if we even have a potentially valid UUID
  // The user may have got here accidentally.
  const looksLikeUUID = /[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}/i.test(
    eventId || ''
  );

  const history = useHistory();
  const [checkInMutation, { loading }] = useMutation(PRE_REG_CHECK_IN);

  const onClose = () => {
    history.push(`/landing`);
  };

  const handleError = () => {
    Alert.alert('Failure', 'Error in Check In. Please try later.', [
      {
        text: 'OK',
        onPress: () => {}
      }
    ]);
  };

  const submitCheckIn = async (values: CheckInForm) => {
    try {
      const res = await checkInMutation({
        variables: {
          lastName: values.lastName?.trim(),
          dateOfBirth: dayjs(values.dob as string, 'MM/DD/YYYY')
            .format()
            .slice(0, -6),
          zipCode: values.zipCode?.trim(),
          eventId
        },
        context: {
          clientName: 'orchestration'
        }
      });

      if (res.data.createPreRegToken.preRegToken) {
        dispatch({
          type: PRE_REGISTRATION_JWT_TOKEN,
          payload: {
            jwtToken: res.data.createPreRegToken.preRegToken.jwt_token,
            eventId
          }
        });

        history.push(`/pre-reg/checklist`);
      } else if (attempts === 2) {
        history.push(`/pre-reg/failed`);
        handleError();
      } else {
        const authFailedData: AmplitudeEventData = {
          currentUrl,
          referringUrl,
          /* eslint-disable @typescript-eslint/camelcase */
          form_location: facilityLocation?.name || '',
          form_detail_additional_info: 'Incorrect Credentials'
        };
        analyticsService.logEvent(AnalyticsEvent.HospitalFormAuthFailed, authFailedData);

        setAttempts(attempts + 1);
        // Handling for failure
        Alert.alert(
          'Failure',
          'The information you entered does not match what we have on file. Please try again.',
          [
            {
              text: 'OK',
              onPress: () => {}
            }
          ]
        );
      }
    } catch (error) {
      handleError();
    }
  };
  // Show error screen instead of login in this instance
  if (!looksLikeUUID) {
    return <ErrorInUUID />;
  }

  return (
    <MuiBox flexDirection="column" alignContent="center">
      <PreRegHeader title="Pre-Visit Check-in" handleClose={onClose} />
      <AuthLiteLogin
        useZip
        submitCheckIn={submitCheckIn}
        attempts={attempts}
        loading={loading}
        message="Please confirm your details below."
        submitText="GET STARTED"
        source="PreRegister"
      />
    </MuiBox>
  );
}

const mapStateToProps = (state: RootState) => ({
  currentUrl: currentLocationPathNameSelector(state),
  referringUrl: previousLocationPathNameSelector(state),
  facilityLocation: PreRegistrationFacilitySelector(state)
});

export default connect(mapStateToProps)(CheckIn as ComponentType);
